// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { type Breakpoint, type SxProps, useTheme } from '@mui/material/styles';
// biome-ignore lint/style/useImportType: <explanation>
import * as React from 'react';
import { Box, Typography } from '../../../lib/base';
import type { Theme } from '../../../theme/Theme';
import { VisibleAtContainer } from '../containers/VisibleAtContainer';
import { getTableStyles } from './getTableStyles';

interface Props {
  readonly name: string | React.ReactElement;
  readonly smallName?: string | React.ReactElement;
  readonly values: Array<string | number | React.ReactElement>;
  readonly numeric?: boolean;
  readonly minWidth?: boolean;
  readonly visibleAt?: Breakpoint;
  readonly firstCol: boolean;
  readonly getRowHeight?: (idx: number) => number | null | undefined;
  readonly flex?: string;
  readonly wrapOverFlow?: boolean;
  readonly typographyStyle?: SxProps<Theme>;
  readonly cellStyle?: SxProps<Theme>;
}
function Column({
  name,
  smallName,
  values,
  numeric,
  minWidth,
  visibleAt,
  firstCol,
  getRowHeight: getRowHeightIn,
  flex: flexIn,
  wrapOverFlow,
  typographyStyle,
  cellStyle,
}: Props): React.ReactElement {
  const classes = getTableStyles(useTheme());
  const getRowHeight = getRowHeightIn || (() => null);
  const wrapValue = (value: string | number | React.ReactElement) =>
    typeof value === 'string' ? (
      <Typography
        sx={{
          ...classes.textRowBase,
          ...classes.row,
          ...(wrapOverFlow ? classes.wrapText : {}),
          ...typographyStyle,
        }}
        variant="body1"
      >
        {value}
      </Typography>
    ) : (
      value
    );
  const cells = values.map((value, idx) => {
    let style = {};
    const rowHeight = getRowHeight(idx);
    if (rowHeight != null) {
      style = { height: rowHeight };
    }
    if (!firstCol) {
      style = { ...style, ...classes.paddingLeft };
    } else {
      style = { ...style, ...classes.firstCol };
    }
    if (numeric !== undefined && numeric) {
      style = { ...style, ...classes.numeric };
    }
    if (idx % 2 === 1) {
      style = { ...style, ...classes.oddRow };
    }
    return (
      <Box
        key={idx}
        sx={{
          ...classes.baseRow,
          ...classes.row,
          ...style,
          ...(wrapOverFlow ? classes.wrapText : {}),
          ...cellStyle,
        }}
      >
        {wrapValue(value)}
      </Box>
    );
  });
  let elementStyle = {};
  if (!minWidth) {
    elementStyle = { ...elementStyle, ...classes.minWidth };
  }
  if (flexIn !== undefined) {
    elementStyle = { ...elementStyle, flex: flexIn };
  }

  let innerElement = {};
  if (!firstCol) {
    innerElement = { ...innerElement, ...classes.paddingLeft };
  } else {
    innerElement = { ...innerElement, ...classes.firstCol };
  }
  if (numeric !== undefined && numeric) {
    innerElement = { ...innerElement, ...classes.numeric };
  }
  const element = (
    <Box
      sx={{
        ...classes.root,
        ...classes.alwaysVisible,
        ...elementStyle,
        ...(visibleAt && {
          display: {
            // xs: 'none',
            // [visibleAt]: 'block',
            '@xs/table': 'none',
            [`@${visibleAt}/table`]: 'block',
          },
        }),
      }}
    >
      <Box
        sx={{
          ...classes.baseRow,
          ...classes.headerRow,
          ...innerElement,
          ...cellStyle,
        }}
      >
        {smallName !== undefined ? (
          <VisibleAtContainer
            up={
              <Typography
                sx={{
                  ...classes.textRowBase,
                  ...classes.header,
                }}
                variant="body1"
              >
                {name}
              </Typography>
            }
            down={
              <Typography
                sx={{
                  ...classes.textRowBase,
                  ...classes.header,
                }}
                variant="body1"
              >
                {smallName}
              </Typography>
            }
          />
        ) : (
          <Typography
            sx={{
              ...classes.textRowBase,
              ...classes.header,
            }}
            variant="body1"
          >
            {name}
          </Typography>
        )}
      </Box>
      {cells}
    </Box>
  );

  return element;
}

export default Column;
